'use client';

/**
 * AvatarTextCell Component
 *
 * Renders avatar (44×44 circular) with primary and secondary text.
 * Follows the specification: primary 14/Semibold, secondary 12/Regular.
 *
 * Typography uses inline styles to avoid Tailwind arbitrary value conflicts.
 * See tokens.ts for detailed explanation of the Tailwind CSS variable issue.
 */

import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { defaultTheme, getTextStyles } from '../theme/tokens';
import { Mail } from 'lucide-react';
import { getInitials } from '@/lib/formatters/people';

interface AvatarTextCellProps {
  primaryText: string;
  secondaryText?: string;
  avatarUrl?: string;
  avatarFallback?: string;
  className?: string;
}

export const AvatarTextCell: React.FC<AvatarTextCellProps> = ({
  primaryText,
  secondaryText,
  avatarUrl,
  avatarFallback,
  className,
}) => {
  // Generate initials from primary text if no fallback provided
  // Blank text yields '' so the mail icon shows instead of a '?'.
  const initials = avatarFallback || (primaryText.trim() ? getInitials(primaryText) : '');

  // Get typography styles from theme (inline styles to avoid Tailwind conflicts)
  const textStyles = getTextStyles(defaultTheme);

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <Avatar className="h-[var(--dt-avatar-size)] w-[var(--dt-avatar-size)] flex-shrink-0">
        <AvatarImage
          src={avatarUrl || '/images/user/avatar-default.png'}
          alt={primaryText}
        />
        <AvatarFallback className="bg-gradient-to-br from-[var(--dt-avatar-fallback-from)] to-[var(--dt-avatar-fallback-to)] text-white text-sm">
          {initials || <Mail className="h-4 w-4" />}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        {/* Primary text: 14px Semibold (inline styles for precision) */}
        <div 
          className="mb-0.5" 
          style={{
            ...textStyles.primary(),
            overflow: 'clip',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {primaryText}
        </div>
        {/* Secondary text: 12px Regular (inline styles for precision) */}
        {secondaryText && (
          <div 
            style={{
              ...textStyles.secondary(),
              overflow: 'clip',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {secondaryText}
          </div>
        )}
      </div>
    </div>
  );
};
